<template>
  <div class="space-y-2" data-test-id="form-sign-in">
    <h2 v-if="$t.contactUsForm.title && showTitle" id="contactUsFormTitle" :class="classTitle">
      {{ $t.contactUsForm.title }}
    </h2>
    <p v-if="$t.contactUsForm.subheading">
      {{ replaceAll($t.contactUsForm.subheading, { brandName: capitalize(brand) }) }}
    </p>
    <p class="text-xs c-grey-20">
      {{ $t.requiredField }}
    </p>
    <base-form
      ref="contactUsFormRef"
      :aria-labelledby="$t.contactUsForm.title && showTitle ? 'contactUsFormTitle' : null"
      class="grid"
      :form
      @submit="submit"
    >
      <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
        <vf-form-field
          v-slot="{ attrs }"
          class-wrapper="overflow-hidden"
          name="category"
          :rule="validateRequired('', $t.validators.requiredSelect)"
        >
          <vf-select
            v-model="form.category"
            v-bind="attrs"
            :disabled="processing"
            :options="categoryOptions"
            required
          >
            {{ $t.contactUsForm.categoryLabel }}
          </vf-select>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          class-wrapper="overflow-hidden"
          name="subject"
          :rule="form.category ? validateRequired('', $t.validators.requiredSelect) : []"
        >
          <vf-select
            v-model="form.subject"
            v-bind="attrs"
            :disabled="processing || !form.category"
            :options="subjectOptions"
            required
          >
            {{ $t.contactUsForm.subjectLabel }}
          </vf-select>
        </vf-form-field>
      </div>
      <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
        <vf-form-field
          v-slot="{ attrs }"
          name="firstName"
          :rule="[
            ...getValidator('firstName', $t.firstName),
            validateRequired($t.firstName),
            validateName($t.validation.firstName),
          ]"
        >
          <vf-input v-model="form.firstName" v-bind="attrs" :disabled="processing" required>
            {{ $t.firstName }}
          </vf-input>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          name="lastName"
          :rule="[
            ...getValidator('lastName', $t.lastName),
            validateRequired($t.lastName),
            validateName($t.validation.lastName),
          ]"
        >
          <vf-input v-model="form.lastName" v-bind="attrs" :disabled="processing" required>
            {{ $t.lastName }}
          </vf-input>
        </vf-form-field>
      </div>
      <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
        <vf-form-field
          v-slot="{ attrs }"
          :hint="$t.emailFormatHint"
          name="emailAddress"
          :rule="[...getValidator('email', $t.email), validateEmail()]"
        >
          <vf-input v-model="form.emailAddress" v-bind="attrs" :disabled="processing" required type="email">
            {{ $t.email }}
          </vf-input>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          name="phone"
          :rule="form.phone !== '' ? [validateRequired($t.phone), validatePhone(form.phoneCode)] : []"
        >
          <input-phone
            v-bind="attrs"
            v-model="form.phone"
            v-model:code="form.phoneCode"
            :disabled="processing"
            use-country-code
          >
            {{ $t.phone }}
          </input-phone>
        </vf-form-field>
      </div>
      <div class="mb-4 gap-x-3 gap-y-4 grid md:cols-2">
        <vf-form-field v-slot="{ attrs }" name="orderNumber" :rule="[...getValidator('orderNumber', $t.orderNumber)]">
          <vf-input v-model="form.orderNumber" v-bind="attrs" :disabled="processing">
            {{ $t.orderNumber }}
          </vf-input>
        </vf-form-field>
      </div>

      <div class="mb-4">
        <vf-form-field
          v-slot="{ attrs }"
          name="message"
          :rule="[validateRequired($t.message), validateRegex(/^[^<>]*$/, $t.noSpecialCharacters)]"
        >
          <vf-textarea
            v-model="form.message"
            v-bind="attrs"
            data-test-id="form-contact-us-message"
            :disabled="processing"

            :props-textarea="{ rows: 11 }"
            required
            resizable
          >
            {{ $t.message }}
          </vf-textarea>
        </vf-form-field>
      </div>

      <!-- reCaptcha -->
      <div class="mb-4">
        <template v-for="item in agreementCheckboxes" :key="item.type">
          <vf-form-field
            v-slot="{ attrs }"
            :name="item.type"
            :rule="('required' in item && item.required) ? [validateRequired('', $t.validators.requiredCheckbox)] : []"
          >
            <vf-checkbox v-model="form[item.type]" v-bind="attrs" required>
              <form-legal-disclaimer
                :content="$t.brandAgreement[item.type]"
                :data-test-ids="{ privacy: 'form-sign-up-link-loyalty' }"
              />
            </vf-checkbox>
          </vf-form-field>
        </template>
      </div>
      <vf-button class="mb-4 w-full md:w-1/3" :disabled="processing" :loading="processing" type="submit">
        {{ messageSuccessfullySent ? $t.messageSent : $t.sendMessage }}
      </vf-button>
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { GetContactUsCategoriesData } from '#root/api/clients/contact-us/data-contracts'
import type { SelectOption } from '#types/components/base/select'

const { showTitle = true } = defineProps<{ showTitle?: boolean, classTitle?: string }>()

const emit = defineEmits<{
  'display-error': [err: any]
}>()
const { brand } = useRuntimeConfig().public
const { contactUs } = useApi()
const countryCode = useCountryCode()

const initFormObject = () => ({
  category: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  message: '',
  orderNumber: '',
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(countryCode),
  subject: ''
})

const categoryOptions = ref<SelectOption[]>([])
const subjectOptions = ref<SelectOption[]>([])
const messageSuccessfullySent = ref(false)
const processing = ref(false)

const contactUsCategoriesResponse = ref<Awaited<GetContactUsCategoriesData>>({} as GetContactUsCategoriesData)
const contactUsFormRef = ref<InstanceType<typeof BaseFormType>>()

const form = reactive(initFormObject())

const { load: loadRules, getValidator } = useAddressApiRules()

const { agreementCheckboxes } = useAppConfig().components.form.contactUs

const { $t } = useNuxtApp()

const recaptcha = useRecaptcha()
const { validateEmail, validateRequired, validatePhone } = useLocalizedValidators()

const resetForm = () => Object.assign(form, initFormObject())
const parseCategoryOptions = () => {
  categoryOptions.value = (contactUsCategoriesResponse.value?.categories || [])
    .map(({ category: value, translation: label }) => ({ value, label }))
}

const parseSubjectOptions = () => {
  form.subject = ''
  subjectOptions.value = contactUsCategoriesResponse.value.categories
    ?.find((cat) => cat.category === form.category)
    ?.subjects
    .map(({ subject: value, translation: label }) => ({ value, label })) || []
}

onMounted(async () => {
  loadRules(countryCode.toLocaleLowerCase(), useLocale())
  contactUsCategoriesResponse.value = await contactUs.$getContactUsCategories()
  parseCategoryOptions()
})

watch(form, () => messageSuccessfullySent.value = false)

watch(() => form.category, () => {
  parseSubjectOptions()
})

const getCountryLanguage = () => {
  const [language, country] = useLocale().split('-')
  return `${country}-${language}`
}
const preparePayload = () => {
  return {
    category: form.category,
    consumerEmail: form.emailAddress,
    firstName: form.firstName,
    lastName: form.lastName,
    message: form.message,
    orderNumber: form.orderNumber,
    origin: getCountryLanguage(),
    personTitle: '',
    consumerPhone: form.phone ? formatE164phone(form.phone, form.phoneCode) : '',
    subject: form.subject
  }
}

const submit = async () => {
  try {
    emit('display-error', { message: '' })
    processing.value = true

    const recaptcha_response = await recaptcha.execute('sendCustomerCareCase')
    const postPayload = preparePayload()
    await contactUs.$sendCase({ ...postPayload, recaptcha_response })
    resetForm()
    contactUsFormRef.value?.reset()
    await nextTick()
    messageSuccessfullySent.value = true
  }
  catch (err) {
    assertApiError(err)
    emit('display-error', err)
    scrollTo({ top: 0, behavior: 'smooth' })
  }
  finally {
    processing.value = false
  }
}
</script>
